import { FirebaseError } from "firebase/app";

const isString = (val?: unknown): val is string => {
    return typeof val === "string" && val.length > 0;
};

const isNumber = (val?: unknown): val is number => {
    return typeof val === "number";
};

const isObject = (val: unknown): val is object => {
    return typeof val === "object" && val !== null;
};

const isDefined = <D>(val: D | undefined): val is D => {
    return typeof val !== "undefined";
};

const hasValue = <D>(val: D | null | undefined): val is D => {
    return typeof val !== "undefined" && val !== null;
};

const isArrayWithData = <D>(val: D | undefined): val is D => {
    return Array.isArray(val) && val.length > 0;
};

// Deliberately permissive: the API validates recipients properly before they
// are stored. This only stops obvious typos from becoming a chip in the list,
// and refuses the characters the API refuses, so that an address shown as
// accepted is rarely rejected on save.
const isEmail = (val: string) => {
    return /^[^\s@"(),:;<>[\\\]]+@[^\s@"(),:;<>[\\\]]+\.[^\s@"(),:;<>[\\\]]+$/.test(val);
};

const isJson = (val: string) => {
    try {
        JSON.parse(val);
        return true;
    } catch (e) {
        return false;
    }
};

const typedKeys = <O extends object>(obj: O) => {
    return Object.keys(obj) as (keyof O)[];
};

const firebaseErrorCodes = [
    "auth/insufficient-permission",
    "auth/invalid-email",
    "auth/invalid-email-verified",
    "auth/invalid-password",
    "auth/invalid-phone-number",
    "auth/invalid-login-credentials",
    "auth/invalid-credential",
    "auth/phone-number-already-exists",
    "auth/email-already-exists",
    "auth/network-request-failed",
    "auth/wrong-password",
    "auth/invalid-action-code",
    "auth/user-not-found",
    "auth/no-current-user",
    "auth/too-many-requests",
];

const ignoreFirebaseError = (error: FirebaseError) => {
    return firebaseErrorCodes.includes(error.code);
};

const validateFileSize = (file: File) => {
    const sizeInKB = Math.ceil(file.size / 1024);
    if (sizeInKB > 5000) throw Error("Maximum allowed image size is 5 MBs. $sentry-ignore");
};

const validateFileType = (file: File) => {
    const type = file.type;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/avif"];

    if (allowedTypes.includes(type) === false) {
        throw new Error(
            "Invalid image type. Allowed types are jpeg/jpg, png, avif, webp. $sentry-ignore",
        );
    }
};

export {
    isString,
    isNumber,
    isObject,
    isDefined,
    hasValue,
    isArrayWithData,
    isEmail,
    isJson,
    ignoreFirebaseError,
    typedKeys,
    validateFileSize,
    validateFileType,
};
